import React, { useState, useEffect } from "react";
import type { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { CHAVE_DAS_CONTAS_SELECIONADAS } from "@/hooks/useEstadoDasFontes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
interface InstagramCardMetric {
  metric: string;
  label: string;
  value: number;
  previous_value?: number;
  change: number;
  trend: "up" | "down" | "neutral";
  current_period: { start_date: string; end_date: string };
  previous_period: { start_date: string; end_date: string };
  has_data: boolean;
  account_warning?: {
    warning_type: string;
    message: string;
    details?: string | null;
  } | null;
  raw_values?: { current: number; previous: number };
}

interface InstagramTimeseriesPoint {
  date: string;
  label: string;
  value: number;
}

interface InstagramTimeseriesMetric {
  metric: string;
  label: string;
  points: InstagramTimeseriesPoint[];
  total_points: number;
  start_date: string;
  end_date: string;
  has_data: boolean;
}

interface InstagramDemographicsMetric {
  metric: string;
  items: {
    gender?: string;
    age_range?: string;
    city?: string;
    count: number;
    percentage?: number;
    color?: string;
  }[];
  total: number;
  start_date: string;
  end_date: string;
  has_data: boolean;
  /** Quando o Instagram foi consultado — a data do retrato, e não a do período (M-011). */
  snapshot_at?: string | null;
}
import {
  getSelectedAccountsApiV1ExternalAccountsSelectedGet,
  getInstagramCardMetricApiV1InstagramCardsMetricSlugGet,
  getInstagramTimeseriesMetricApiV1InstagramTimeseriesMetricSlugGet,
  getInstagramDemographicsDimensionApiV1InstagramDemographicsDimensionSlugGet,
  getInstagramEngagementApiV1InstagramEngagementGet,
  getInstagramStoriesApiV1InstagramStoriesGet,
  getInstagramPostsApiV1InstagramPostsGet,
} from "@/backend-sdk/client/sdk.gen";
import { useAuth } from "@/contexts/auth.context";
import { useAccount } from "@/contexts/account.context";
import { heyApiClient, tenantAtivo } from "@/lib/hey-api-client";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import type { KpiDeReel } from "@/components/instagram/InstagramReelsSection";
import {
  LineChart as TremorLineChart,
  BarChart as TremorBarChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  formatPercent,
  formatInteger,
  formatSignedPercent,
} from "@/utils/numberFormat";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import { Skeleton } from "@/components/ui/skeleton";
import type { TableQueryDTO } from "@/types/table-query";
import { differenceInCalendarDays, subDays } from "date-fns";

// Sub-components
import {
  InstagramPostsTimeline,
  InstagramPostDetailCard,
  InstagramStoriesTable,
  InstagramReelsSection,
  InstagramBestTimes,
  InstagramEngagementRates,
  InstagramDemographics,
} from "@/components/instagram";

// Types
import type {
  InstagramPostRow,
  InstagramPostDetail,
  InstagramStoryRow,
  InstagramReelKpi,
  InstagramEngagementRate,
  BestTimeDataPoint,
  InstagramExtraKpis,
  InstagramEngagementApiResponse,
  InstagramStoriesApiResponse,
  InstagramPostsApiResponse,
} from "@/components/instagram";

// =============================================================================
// MOCK DATA — will be replaced with API integration later
// =============================================================================

const MOCK_ENGAGEMENT_RATES: InstagramEngagementRate[] = [
  {
    title: "Engajamento por Seguidores",
    value: 0.48,
    change: 24.5,
    trend: "down",
  },
  {
    title: "Engajamento por Visualizações",
    value: 2.97,
    change: 51.0,
    trend: "up",
  },
  {
    title: "Engajamento por Alcance",
    value: 0.05,
    change: 32.4,
    trend: "down",
  },
];

const MOCK_EXTRA_KPIS: InstagramExtraKpis = {
  seguindo: { value: 266, change: 0, trend: "neutral" },
  engajamentosMidia: { value: 1532, change: 19.9, trend: "down" },
  alcanceTotal: { value: 2030623, change: 6.7, trend: "up" },
  sessoes: {
    value: 6,
    change: 100.0,
    trend: "up",
    detail: "utm_source=linktbio",
  },
  totalPosts: 180,
};

const MOCK_POST_DETAIL: InstagramPostDetail = {
  thumbnail: null,
  caption:
    "O empreendedor é aquele que transforma visão em execução e inspiração em resultado mensurável.",
  date: "19/04/2025",
  type: "Reels",
  views: 1808,
  reach: 671,
  tx: 15,
  likes: 1085,
  comments: 15,
  saves: 1,
  shares: 1,
  score: 1,
};

const MOCK_BEST_DAY_DATA: BestTimeDataPoint[] = [
  { label: "Seg", value: 1259 },
  { label: "Ter", value: 724 },
  { label: "Qua", value: 55 },
  { label: "Qui", value: 4 },
  { label: "Sex", value: 43 },
  { label: "Sáb", value: 4 },
  { label: "Dom", value: 85 },
];

const MOCK_BEST_HOUR_DATA: BestTimeDataPoint[] = [
  { label: "0h", value: 200 },
  { label: "1h", value: 120 },
  { label: "2h", value: 80 },
  { label: "3h", value: 60 },
  { label: "4h", value: 40 },
  { label: "5h", value: 100 },
  { label: "6h", value: 350 },
  { label: "7h", value: 600 },
  { label: "8h", value: 900 },
  { label: "9h", value: 1200 },
  { label: "10h", value: 1500 },
  { label: "11h", value: 1800 },
  { label: "12h", value: 2100 },
  { label: "13h", value: 1900 },
  { label: "14h", value: 1700 },
  { label: "15h", value: 1400 },
  { label: "16h", value: 1100 },
  { label: "17h", value: 1300 },
  { label: "18h", value: 1600 },
  { label: "19h", value: 2000 },
  { label: "20h", value: 2400 },
  { label: "21h", value: 3200 },
  { label: "22h", value: 2800 },
  { label: "23h", value: 1000 },
];

const MOCK_REELS_KPIS: InstagramReelKpi[] = [
  {
    key: "reelsCount",
    title: "Número de Reels",
    value: 11,
    change: 100,
    trend: "up",
  },
  {
    key: "reelsReach",
    title: "Alcance dos Reels dos últimos 90 dias",
    value: 2158,
    change: 100,
    trend: "up",
  },
  {
    key: "reelsInteractions",
    title: "Interações nos Reels",
    value: 234,
    change: 100,
    trend: "up",
  },
  {
    key: "reelsLikes",
    title: "Curtidas nos Reels",
    value: 159,
    change: 154.8,
    trend: "up",
  },
  {
    key: "reelsComments",
    title: "Comentários nos Reels",
    value: 11,
    change: 100,
    trend: "up",
  },
  {
    key: "reelsSaves",
    title: "Salvamento nos Reels",
    value: 18,
    change: 100,
    trend: "up",
  },
  {
    key: "reelsShares",
    title: "Compartilhamento nos Reels",
    value: 23,
    change: 100,
    trend: "up",
  },
];

// MOCK_STORIES_DATA removed — Stories now fetched from /instagram-stories API

// =============================================================================
// CARD METRIC CONFIG — real API-driven KPIs
// =============================================================================

type CardMetricConfig = {
  key: string;
  slug: string;
  title: string;
  format: "number" | "percentage";
};

const CARD_METRICS_CONFIG: CardMetricConfig[] = [
  {
    key: "seguidores",
    slug: "seguidores",
    title: "Seguidores",
    format: "number",
  },
  { key: "seguindo", slug: "seguindo", title: "Seguindo", format: "number" },
  {
    key: "totalPostsPublicados",
    slug: "total-posts-publicados",
    title: "Total de Posts Publicados",
    format: "number",
  },
  { key: "sessoes", slug: "sessoes", title: "Sessões", format: "number" },
  {
    key: "reelsCount",
    slug: "reels-count",
    title: "Número de Reels",
    format: "number",
  },
  {
    key: "reelsReach90d",
    slug: "reels-reach-90d",
    title: "Alcance dos Reels dos últimos 90 dias",
    format: "number",
  },
  {
    key: "reelsInteractions",
    slug: "reels-interactions",
    title: "Interações nos Reels",
    format: "number",
  },
  {
    key: "reelsLikes",
    slug: "reels-likes",
    title: "Curtidas nos Reels",
    format: "number",
  },
  {
    key: "reelsComments",
    slug: "reels-comments",
    title: "Comentários nos Reels",
    format: "number",
  },
  {
    key: "reelsSaves",
    slug: "reels-saves",
    title: "Salvamento nos Reels",
    format: "number",
  },
  {
    key: "reelsShares",
    slug: "reels-shares",
    title: "Compartilhamento nos Reels",
    format: "number",
  },
  { key: "alcance", slug: "alcance", title: "Alcance", format: "number" },
  {
    key: "impressoes",
    slug: "visualizacoes",
    title: "Visualizações",
    format: "number",
  },
  {
    key: "novosSeguidores",
    slug: "novos-seguidores",
    title: "Novos Seguidores",
    format: "number",
  },
  { key: "curtidas", slug: "curtidas", title: "Curtidas", format: "number" },
  {
    key: "comentarios",
    slug: "comentarios",
    title: "Comentários",
    format: "number",
  },
  { key: "salvos", slug: "salvos", title: "Salvos", format: "number" },
  {
    key: "compartilhamentos",
    slug: "compartilhamentos",
    title: "Compartilhamentos",
    format: "number",
  },
  {
    key: "taxaEngajamento",
    slug: "taxa-engajamento",
    title: "Taxa de Engajamento",
    format: "percentage",
  },
  {
    key: "visitasAoPerfil",
    slug: "visitas-perfil",
    title: "Visitas ao Perfil",
    format: "number",
  },
  {
    key: "cliquesLinkBio",
    slug: "cliques-link-bio",
    title: "Cliques Link Bio",
    format: "number",
  },
  {
    key: "cliquesSite",
    slug: "cliques-site",
    title: "Cliques no Site",
    format: "number",
  },
];

type TimeseriesConfig = {
  slug: "alcance" | "visualizacoes" | "novos-seguidores" | "visitas-perfil";
  title: string;
  color: string;
  chart: "line" | "bar";
};

const TIMESERIES_CONFIG: TimeseriesConfig[] = [
  {
    slug: "alcance",
    title: "Alcance por Dia",
    color: "#8884d8",
    chart: "line",
  },
  {
    slug: "visualizacoes",
    title: "Visualizações por Dia",
    color: "#82ca9d",
    chart: "line",
  },
  {
    slug: "novos-seguidores",
    title: "Novos Seguidores por Dia",
    color: "#fbbf24",
    chart: "bar",
  },
  {
    slug: "visitas-perfil",
    title: "Visitas ao Perfil por Dia",
    color: "#ef4444",
    chart: "line",
  },
];

/** Todos os números da tela saem da conta do Instagram escolhida para a loja. */
const FONTES_DO_INSTAGRAM: readonly RequisitoDeFonte[] = ["instagram"];

/** O número de um card, ou `null` quando a métrica não veio — para desenhar N/D, e não zero. */
const numeroDoCard = (
  metric: InstagramCardMetric | undefined,
): number | null =>
  typeof metric?.value === "number" && Number.isFinite(metric.value)
    ? metric.value
    : null;

const DEMOGRAPHICS_CONFIG = [
  { slug: "gender", key: "gender" },
  { slug: "age", key: "age" },
  { slug: "city", key: "city" },
];

// =============================================================================
// SHARED UI — Trend indicator for real API-driven cards
// =============================================================================

function TrendIndicator({
  value,
  trend,
}: {
  value: number;
  trend: "up" | "down" | "neutral";
}): React.ReactElement | null {
  const { filter } = useGlobalFilter();
  if (!isComparisonEnabled(filter)) return null;
  if (trend === "neutral" || value === 0) return null;
  const Icon = trend === "up" ? TrendingUp : TrendingDown;
  const colorClass = trend === "up" ? "text-green-500" : "text-red-500";
  return (
    <div className={`flex items-center gap-1 text-sm ${colorClass}`}>
      <Icon className="w-4 h-4" />
      <span>{formatSignedPercent(value, 1)}</span>
    </div>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT — Orchestrator
// =============================================================================

const InstagramPage = (): React.ReactElement => {
  const { filter: filterState } = useGlobalFilter();
  const { user } = useAuth();
  const { selectedAccount } = useAccount();
  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    null;

  // State: real API data
  const [cardMetrics, setCardMetrics] = useState<
    Record<string, InstagramCardMetric | undefined>
  >({});
  const [timeseriesMetrics, setTimeseriesMetrics] = useState<
    Record<string, InstagramTimeseriesMetric | undefined>
  >({});
  const [demographicsMetrics, setDemographicsMetrics] = useState<
    Record<string, InstagramDemographicsMetric | undefined>
  >({});
  // Começa carregando: com `false`, o primeiro desenho mostrava os cards com "-" antes da busca.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [erroMetricas, setErroMetricas] = useState(false);

  // A conta do Instagram da loja, numa consulta por loja: trocar de loja não recarrega a
  // página, e um id guardado em estado seria mandado às rotas da loja seguinte. <D-089> A
  // loja vai em `customer_uuid`, e não no token de conta, que vence com o painel parado.
  // Pendurada no prefixo das contas selecionadas, ela é refeita quando a seleção muda.
  const tenant = tenantAtivo();
  const contaInstagram = useQuery({
    queryKey: [CHAVE_DAS_CONTAS_SELECIONADAS, "instagram", tenant],
    queryFn: async () => {
      const { data: resp } =
        await getSelectedAccountsApiV1ExternalAccountsSelectedGet({
          client: heyApiClient,
          throwOnError: true,
          query: { source: "meta", customer_uuid: tenant },
        });
      // A rota devolve a grafia do banco, `instagram_insights`; `instagram` é a grafia antiga.
      const ig = (resp.accounts || []).find(
        (a) => a.type === "instagram_insights" || a.type === "instagram",
      );
      return ig?.external_id || ig?.id || null;
    },
    enabled: Boolean(tenant),
    staleTime: 60_000,
    // A conta é opcional (sem ela o backend resolve a padrão): uma falha não segura a tela.
    retry: false,
  });
  const aguardandoContaInstagram = contaInstagram.isLoading;
  const instagramAccountId = contaInstagram.data ?? undefined;
  const [accountWarning, setAccountWarning] = useState<{
    message: string;
    details?: string;
  } | null>(null);

  // State: Engagement API data
  const [engagementRates, setEngagementRates] = useState<
    InstagramEngagementRate[]
  >([]);
  const [engagementRaw, setEngagementRaw] =
    useState<InstagramEngagementApiResponse | null>(null);

  // State: Stories API data
  const [storiesData, setStoriesData] = useState<InstagramStoryRow[]>([]);
  // Começa ligado, como `loading`: com `false`, a tabela dizia "Nenhum dado encontrado"
  // antes de a busca sair.
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [storiesPage, setStoriesPage] = useState(1);
  const [storiesTotal, setStoriesTotal] = useState(0);
  const [storiesTotalRow, setStoriesTotalRow] = useState<
    InstagramStoriesApiResponse["total_row"] | null
  >(null);
  const [storiesSortBy, setStoriesSortBy] = useState("date");
  const [storiesSortOrder, setStoriesSortOrder] = useState<"asc" | "desc">(
    "desc",
  );
  const [storiesSearch, setStoriesSearch] = useState<string | undefined>(
    undefined,
  );
  const [storiesFilters, setStoriesFilters] =
    useState<TableQueryDTO["filters"]>(undefined);

  // State: Posts Timeline API data
  const [postsData, setPostsData] = useState<InstagramPostRow[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsPage, setPostsPage] = useState(1);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsTotalRow, setPostsTotalRow] = useState<
    InstagramPostsApiResponse["total_row"] | null
  >(null);
  const [postsSortBy, setPostsSortBy] = useState("date");
  const [postsSortOrder, setPostsSortOrder] = useState<"asc" | "desc">("desc");
  const [postsSearch, setPostsSearch] = useState<string | undefined>(undefined);
  const [postsFilters, setPostsFilters] =
    useState<TableQueryDTO["filters"]>(undefined);

  // Helpers
  const getCardMetric = (key: string) => cardMetrics[key];

  const getTimeseriesPoints = (slug: TimeseriesConfig["slug"]) =>
    timeseriesMetrics[slug]?.points ?? [];

  const genderMetric = demographicsMetrics["gender"];
  const ageMetric = demographicsMetrics["age"];
  const cityMetric = demographicsMetrics["city"];

  const genderChartData =
    genderMetric?.items.map((item) => ({
      name: item.gender ?? "N/D",
      percentage: item.percentage ?? 0,
      count: item.count ?? 0,
      color: item.color ?? "#888888",
    })) ?? [];

  const ageChartData =
    ageMetric?.items.map((item) => ({
      name: item.age_range ?? "N/D",
      percentage: item.percentage ?? 0,
      count: item.count ?? 0,
      color: item.color ?? "#888888",
    })) ?? [];

  const citiesData = cityMetric?.items ?? [];

  // Os três recortes vêm da mesma busca, então qualquer um deles data o retrato.
  const demographicsSnapshotAt =
    genderMetric?.snapshot_at ??
    ageMetric?.snapshot_at ??
    cityMetric?.snapshot_at ??
    null;

  const hasCardData = Object.values(cardMetrics).some(
    (metric) => metric?.has_data,
  );

  const reelsKpis: KpiDeReel[] = [
    {
      key: "reelsCount",
      title: "Número de Reels",
      value: numeroDoCard(getCardMetric("reelsCount")),
      change: Number(getCardMetric("reelsCount")?.change ?? 0),
      trend: (getCardMetric("reelsCount")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsReach90d",
      title: "Alcance dos Reels dos últimos 90 dias",
      value: numeroDoCard(getCardMetric("reelsReach90d")),
      change: Number(getCardMetric("reelsReach90d")?.change ?? 0),
      trend: (getCardMetric("reelsReach90d")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsInteractions",
      title: "Interações nos Reels",
      value: numeroDoCard(getCardMetric("reelsInteractions")),
      change: Number(getCardMetric("reelsInteractions")?.change ?? 0),
      trend: (getCardMetric("reelsInteractions")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsLikes",
      title: "Curtidas nos Reels",
      value: numeroDoCard(getCardMetric("reelsLikes")),
      change: Number(getCardMetric("reelsLikes")?.change ?? 0),
      trend: (getCardMetric("reelsLikes")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsComments",
      title: "Comentários nos Reels",
      value: numeroDoCard(getCardMetric("reelsComments")),
      change: Number(getCardMetric("reelsComments")?.change ?? 0),
      trend: (getCardMetric("reelsComments")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsSaves",
      title: "Salvamento nos Reels",
      value: numeroDoCard(getCardMetric("reelsSaves")),
      change: Number(getCardMetric("reelsSaves")?.change ?? 0),
      trend: (getCardMetric("reelsSaves")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
    {
      key: "reelsShares",
      title: "Compartilhamento nos Reels",
      value: numeroDoCard(getCardMetric("reelsShares")),
      change: Number(getCardMetric("reelsShares")?.change ?? 0),
      trend: (getCardMetric("reelsShares")?.trend ?? "neutral") as
        | "up"
        | "down"
        | "neutral",
    },
  ];

  const postDetailData: InstagramPostDetail = React.useMemo(() => {
    const firstPost = postsData[0];
    if (!firstPost) {
      return MOCK_POST_DETAIL;
    }

    return {
      thumbnail: firstPost.thumbnail,
      caption: firstPost.caption,
      date: firstPost.date,
      type:
        firstPost.mediaType === "reel"
          ? "Reels"
          : firstPost.mediaType === "carousel"
            ? "Carousel"
            : "Post",
      views: firstPost.views,
      reach: firstPost.reach,
      tx: Number(firstPost.interactionRate ?? 0),
      likes: firstPost.likes,
      comments: firstPost.comments,
      saves: firstPost.saves,
      shares: firstPost.shares,
      score: firstPost.interactions,
    };
  }, [postsData]);

  // Render a single API-driven KPI card
  const renderMetricCard = (
    metricKey: string,
    title: string,
    className: string,
  ) => (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-1">
            <span className="truncate">{title}</span>
            <HelpTooltip
              content={getKpiTooltip(title)}
              triggerClassName="h-3.5 w-3.5 text-[9px]"
            />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start min-w-0">
          <div className="min-w-0 flex-1">
            {(() => {
              const valor = numeroDoCard(getCardMetric(metricKey));
              const config = CARD_METRICS_CONFIG.find(
                (item) => item.key === metricKey,
              );
              const percentual = config?.format === "percentage";
              return (
                <EstadoDoValor
                  carregando={loading}
                  disponivel={valor !== null && config !== undefined}
                  fontes={FONTES_DO_INSTAGRAM}
                  erro={erroMetricas}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={valor ?? 0}
                    format={(n: number) =>
                      percentual ? formatPercent(n) : formatInteger(n)
                    }
                    className="font-light text-foreground"
                    maxFontPx={percentual ? 28 : 30}
                    breathingRoomPx={18}
                    ariaLabel={title}
                  />
                </EstadoDoValor>
              );
            })()}
          </div>
          {(() => {
            const metric = getCardMetric(metricKey);
            if (!metric)
              return <div className="text-sm text-muted-foreground">-</div>;
            return (
              <TrendIndicator value={metric.change} trend={metric.trend} />
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );

  // Render a KPI card with data from the Engagement API (no mock badge)
  const renderMetricCardFromEngagement = (
    title: string,
    value: number | undefined,
    cardClass: string,
  ) => (
    <Card className={cardClass}>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-1">
            <span className="truncate">{title}</span>
            <HelpTooltip
              content={getKpiTooltip(title)}
              triggerClassName="h-3.5 w-3.5 text-[9px]"
            />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-start min-w-0">
          <EstadoDoValor
            carregando={loading}
            disponivel={typeof value === "number" && Number.isFinite(value)}
            fontes={FONTES_DO_INSTAGRAM}
            erro={erroMetricas}
            className="text-2xl font-light text-muted-foreground"
          >
            <KpiNumber
              value={value ?? 0}
              format={(n: number) => formatInteger(n)}
              className="font-light text-foreground"
              maxFontPx={30}
              breathingRoomPx={18}
              ariaLabel={title}
            />
          </EstadoDoValor>
        </div>
      </CardContent>
    </Card>
  );

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  useEffect(() => {
    // A resposta de um filtro anterior não pode sobrescrever a do filtro atual.
    let ativo = true;

    const fetchData = async () => {
      const uuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;

      // A tela segue carregando: buscar sem a conta e de novo com ela dobraria as requisições.
      if (aguardandoContaInstagram) return;

      if (!uuid || !filterState.dateRange) {
        console.log("⚠️ [Instagram] Aguardando customer_uuid ou dateRange");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setErroMetricas(false);
      setAccountWarning(null);

      try {
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const previousEndDate = subDays(filterState.dateRange.startDate, 1);
        const previousStartDate = subDays(
          previousEndDate,
          Math.max(
            differenceInCalendarDays(
              filterState.dateRange.endDate,
              filterState.dateRange.startDate,
            ),
            0,
          ),
        );
        const prevStart = formatDateToISO(previousStartDate);
        const prevEnd = formatDateToISO(previousEndDate);

        // Opcional: sem a conta, o backend resolve a padrão.
        const accountIdToUse = instagramAccountId;

        // Card metrics
        const cardEntries = await Promise.all(
          CARD_METRICS_CONFIG.map(async (config) => {
            const { data: metric } =
              await getInstagramCardMetricApiV1InstagramCardsMetricSlugGet({
                client: heyApiClient,
                throwOnError: true,
                path: { metric_slug: config.slug },
                query: {
                  customer_uuid: uuid!,
                  start_date: startDate,
                  end_date: endDate,
                  previous_start_date: prevStart,
                  previous_end_date: prevEnd,
                  account_id: accountIdToUse ?? null,
                },
              });
            return [config.key, metric] as unknown as [
              string,
              InstagramCardMetric,
            ];
          }),
        );
        if (!ativo) return;
        setCardMetrics(Object.fromEntries(cardEntries));

        const anyWarning = cardEntries
          .map(([, m]) => m.account_warning)
          .find(Boolean);
        if (anyWarning?.message) {
          setAccountWarning({
            message: anyWarning.message,
            details: anyWarning.details ?? undefined,
          });
        }

        // Timeseries
        const timeseriesEntries = await Promise.all(
          TIMESERIES_CONFIG.map(async (config) => {
            const { data: series } =
              await getInstagramTimeseriesMetricApiV1InstagramTimeseriesMetricSlugGet(
                {
                  client: heyApiClient,
                  throwOnError: true,
                  path: { metric_slug: config.slug },
                  query: {
                    customer_uuid: uuid!,
                    start_date: startDate,
                    end_date: endDate,
                    account_id: accountIdToUse ?? null,
                  },
                },
              );
            return [config.slug, series] as unknown as [
              string,
              InstagramTimeseriesMetric,
            ];
          }),
        );
        if (!ativo) return;
        setTimeseriesMetrics(Object.fromEntries(timeseriesEntries));

        // Demographics
        const demographicsEntries = await Promise.all(
          DEMOGRAPHICS_CONFIG.map(async (config) => {
            const { data: demographic } =
              await getInstagramDemographicsDimensionApiV1InstagramDemographicsDimensionSlugGet(
                {
                  client: heyApiClient,
                  throwOnError: true,
                  path: { dimension_slug: config.slug },
                  query: {
                    customer_uuid: uuid!,
                    start_date: startDate,
                    end_date: endDate,
                    account_id: accountIdToUse ?? null,
                  },
                },
              );
            return [config.slug, demographic] as unknown as [
              string,
              InstagramDemographicsMetric,
            ];
          }),
        );
        if (!ativo) return;
        setDemographicsMetrics(Object.fromEntries(demographicsEntries));

        // Engagement (current + previous for comparison)
        const [engCurrent, engPrevious] = await Promise.all([
          getInstagramEngagementApiV1InstagramEngagementGet({
            client: heyApiClient,
            throwOnError: true,
            query: {
              customer_uuid: uuid!,
              start_date: startDate,
              end_date: endDate,
              account_id: accountIdToUse ?? null,
            },
          }).then((r) => r.data),
          getInstagramEngagementApiV1InstagramEngagementGet({
            client: heyApiClient,
            throwOnError: true,
            query: {
              customer_uuid: uuid!,
              start_date: prevStart,
              end_date: prevEnd,
              account_id: accountIdToUse ?? null,
            },
          }).then((r) => r.data),
        ]);
        if (!ativo) return;

        const currentEngagement = engCurrent as InstagramEngagementApiResponse;
        const previousEngagement =
          engPrevious as InstagramEngagementApiResponse;

        const requiredEngagementFields = [
          currentEngagement.engagement_by_followers,
          currentEngagement.engagement_by_reach,
          currentEngagement.total_interactions,
          currentEngagement.total_reach,
          previousEngagement.engagement_by_followers,
          previousEngagement.engagement_by_reach,
        ];
        if (
          requiredEngagementFields.some(
            (value) => typeof value !== "number" || !Number.isFinite(value),
          )
        ) {
          throw new Error(
            "Payload de engagement inválido: campos obrigatórios ausentes ou inválidos.",
          );
        }
        setEngagementRaw(currentEngagement);

        const computeChange = (
          current: number,
          previous: number,
        ): { change: number; trend: "up" | "down" | "neutral" } => {
          if (previous === 0)
            return {
              change: current > 0 ? 100 : 0,
              trend: current > 0 ? "up" : "neutral",
            };
          const pctChange = ((current - previous) / Math.abs(previous)) * 100;
          // O sinal fica no valor: quem desenha é que decide a cor e a seta, e o número
          // continua dizendo a direção mesmo fora da tela (item 34).
          return {
            change: pctChange,
            trend: pctChange > 0 ? "up" : pctChange < 0 ? "down" : "neutral",
          };
        };

        const rates: InstagramEngagementRate[] = [
          {
            title: "Engajamento por Seguidores",
            value: currentEngagement.engagement_by_followers,
            ...computeChange(
              currentEngagement.engagement_by_followers,
              previousEngagement.engagement_by_followers,
            ),
          },
          {
            title: "Engajamento por Visualizações",
            value: currentEngagement.engagement_by_impressions ?? 0,
            ...computeChange(
              currentEngagement.engagement_by_impressions ?? 0,
              previousEngagement.engagement_by_impressions ?? 0,
            ),
          },
          {
            title: "Engajamento por Alcance",
            value: currentEngagement.engagement_by_reach,
            ...computeChange(
              currentEngagement.engagement_by_reach,
              previousEngagement.engagement_by_reach,
            ),
          },
        ];
        setEngagementRates(rates);
      } catch (err) {
        if (!ativo) return;
        console.error("❌ [Instagram] Erro ao buscar dados:", err);
        setErroMetricas(true);
        const axiosError = err as AxiosError<{ detail?: string }>;
        const responseDetail = axiosError.response?.data?.detail;
        const errorMessage =
          err instanceof Error ? err.message : "Erro desconhecido";
        setError(
          responseDetail ??
            errorMessage ??
            "Erro ao carregar dados do Instagram",
        );
        setCardMetrics({});
        setTimeseriesMetrics({});
        setDemographicsMetrics({});
        setEngagementRates([]);
        setEngagementRaw(null);
        setStoriesData([]);
        setStoriesTotalRow(null);
        setPostsData([]);
        setPostsTotalRow(null);
      } finally {
        if (ativo) setLoading(false);
      }
    };

    fetchData();
    return () => {
      ativo = false;
    };
  }, [
    selectedAccount?.customer_uuid,
    filterState.dateRange,
    instagramAccountId,
    aguardandoContaInstagram,
  ]);

  useEffect(() => {
    const fetchStories = async () => {
      const uuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;

      // Esperando a conta, a tabela segue carregando; sem loja ou período, não há o que buscar.
      if (aguardandoContaInstagram) return;
      if (!uuid || !filterState.dateRange) {
        setStoriesLoading(false);
        return;
      }

      setStoriesLoading(true);
      try {
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const { data: storiesResponse } =
          await getInstagramStoriesApiV1InstagramStoriesGet({
            client: heyApiClient,
            throwOnError: true,
            query: {
              customer_uuid: uuid!,
              start_date: startDate,
              end_date: endDate,
              account_id: instagramAccountId ?? null,
              page: storiesPage,
              page_size: 10,
              sort_by: storiesSortBy,
              sort_order: storiesSortOrder,
              q: storiesSearch ?? null,
              filters: storiesFilters?.length
                ? JSON.stringify(storiesFilters)
                : null,
            },
          });
        if (!Array.isArray(storiesResponse.results)) {
          throw new Error(
            'Payload de stories inválido: "results" não é uma lista.',
          );
        }
        setStoriesData(
          storiesResponse.results as unknown as InstagramStoryRow[],
        );
        setStoriesTotalRow(
          (storiesResponse.total_row ||
            null) as unknown as InstagramStoriesApiResponse["total_row"],
        );
        setStoriesTotal(
          storiesResponse.total ?? storiesResponse.results.length,
        );
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>;
        const responseDetail = axiosError.response?.data?.detail;
        const errorMessage =
          err instanceof Error ? err.message : "Erro desconhecido";
        setError(
          responseDetail ??
            errorMessage ??
            "Erro ao carregar stories do Instagram",
        );
        setStoriesData([]);
        setStoriesTotalRow(null);
        setStoriesTotal(0);
      } finally {
        setStoriesLoading(false);
      }
    };

    fetchStories();
  }, [
    selectedAccount?.customer_uuid,
    filterState.dateRange,
    instagramAccountId,
    aguardandoContaInstagram,
    storiesPage,
    storiesSortBy,
    storiesSortOrder,
    storiesSearch,
    storiesFilters,
  ]);

  const handleStoriesQueryChange = (query: TableQueryDTO): void => {
    const allowedSortBy = new Set([
      "date",
      "story_id",
      "impressions",
      "reach",
      "replies",
      "exits",
      "taps_forward",
      "taps_back",
      "retention_rate",
    ]);

    if (query.sortBy && allowedSortBy.has(query.sortBy)) {
      setStoriesSortBy(query.sortBy);
    } else {
      setStoriesSortBy("date");
    }

    setStoriesSortOrder(query.sortDir === "asc" ? "asc" : "desc");
    setStoriesSearch(query.q?.trim() ? query.q.trim() : undefined);
    setStoriesFilters(query.filters?.length ? query.filters : undefined);
    setStoriesPage(1);
  };

  useEffect(() => {
    const fetchPostsTimeline = async () => {
      const uuid =
        selectedAccount?.customer_uuid ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;

      if (!uuid || !filterState.dateRange) {
        setPostsLoading(false);
        return;
      }

      setPostsLoading(true);
      try {
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const { data: postsResponse } =
          await getInstagramPostsApiV1InstagramPostsGet({
            client: heyApiClient,
            throwOnError: true,
            query: {
              customer_uuid: uuid!,
              start_date: startDate,
              end_date: endDate,
              account_id: null,
              page: postsPage,
              page_size: 10,
              sort_by: postsSortBy,
              sort_order: postsSortOrder,
              q: postsSearch ?? null,
              filters: postsFilters?.length
                ? JSON.stringify(postsFilters)
                : null,
            },
          });
        if (!Array.isArray(postsResponse.results)) {
          throw new Error(
            'Payload de posts inválido: "results" não é uma lista.',
          );
        }
        setPostsData(
          (postsResponse.results ?? []).map((p) => {
            return {
              ...p,
              interactionRate: p.interactionRate ?? 0,
            } as InstagramPostRow;
          }),
        );
        setPostsTotalRow(
          (postsResponse.total_row ||
            null) as unknown as InstagramPostsApiResponse["total_row"],
        );
        setPostsTotal(postsResponse.total ?? postsResponse.results.length);
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>;
        const responseDetail = axiosError.response?.data?.detail;
        const errorMessage =
          err instanceof Error ? err.message : "Erro desconhecido";
        setError(
          responseDetail ??
            errorMessage ??
            "Erro ao carregar posts do Instagram",
        );
        setPostsData([]);
        setPostsTotalRow(null);
        setPostsTotal(0);
      } finally {
        setPostsLoading(false);
      }
    };

    fetchPostsTimeline();
  }, [
    selectedAccount?.customer_uuid,
    filterState.dateRange,
    postsPage,
    postsSortBy,
    postsSortOrder,
    postsSearch,
    postsFilters,
  ]);

  const handlePostsQueryChange = (query: TableQueryDTO): void => {
    const allowedSortBy = new Set([
      "thumbnail",
      "mediaType",
      "date",
      "caption",
      "reach",
      "views",
      "saves",
      "likes",
      "comments",
      "shares",
      "interactions",
      "interactionRate",
    ]);

    if (query.sortBy && allowedSortBy.has(query.sortBy)) {
      setPostsSortBy(query.sortBy);
    } else {
      setPostsSortBy("date");
    }

    setPostsSortOrder(query.sortDir === "asc" ? "asc" : "desc");
    setPostsSearch(query.q?.trim() ? query.q.trim() : undefined);
    setPostsFilters(query.filters?.length ? query.filters : undefined);
    setPostsPage(1);
  };

  // =========================================================================
  // SKELETON — full-page loading state
  // =========================================================================

  const renderPageSkeleton = () => (
    <div className="space-y-6">
      {/* Row 1: 7 KPI skeletons */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
        {[...Array(7)].map((_, i) => (
          <Card key={`r1-${i}`} className="card-kpi-primary">
            <CardHeader>
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Row 2: 6 KPI skeletons */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[...Array(6)].map((_, i) => (
          <Card key={`r2-${i}`} className="card-kpi-primary">
            <CardHeader>
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Engagement rate skeletons */}
      <div className="grid gap-4 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={`eng-${i}`} className="card-kpi-secondary">
            <CardHeader>
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Formula skeleton */}
      <Card className="border-dashed border-muted-foreground/30">
        <CardContent className="py-3 px-4">
          <Skeleton className="h-5 w-full" />
        </CardContent>
      </Card>
      {/* Demographics skeletons */}
      <div className="grid gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={`demo-${i}`}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Posts table skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`post-${i}`} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
      {/* Post detail skeleton */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex gap-4">
            <Skeleton className="size-12 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
      {/* Best times skeleton */}
      <div className="grid gap-6 md:grid-cols-2">
        {[...Array(2)].map((_, i) => (
          <Card key={`bt-${i}`}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-52 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Reels skeleton */}
      <div>
        <Skeleton className="h-6 w-20 mb-4" />
        <div className="grid gap-6 md:grid-cols-3 mb-6">
          {[...Array(3)].map((_, i) => (
            <Card key={`reel-${i}`} className="card-kpi-primary">
              <CardHeader>
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={`reel2-${i}`} className="card-kpi-secondary">
              <CardHeader>
                <Skeleton className="h-4 w-28" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {/* Stories skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`story-${i}`} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
      {/* Time series skeletons */}
      {[...Array(3)].map((_, i) => (
        <Card key={`ts-${i}`}>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-52 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="p-6 space-y-6 page-background">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Instagram</h1>
        <p className="text-muted-foreground">
          Dashboard de performance e métricas do Instagram Insights
        </p>
      </div>

      {/* Global filter + Total Posts indicator */}
      <GlobalFilter
        rightSlot={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted/50 border border-border/60 text-xs">
              <span className="text-muted-foreground">
                Total de Posts Publicados
              </span>
              <span className="font-semibold text-foreground">
                {(() => {
                  const totalPosts = numeroDoCard(
                    getCardMetric("totalPostsPublicados"),
                  );
                  return (
                    <EstadoDoValor
                      carregando={loading}
                      disponivel={totalPosts !== null}
                      fontes={FONTES_DO_INSTAGRAM}
                      erro={erroMetricas}
                    >
                      {formatInteger(totalPosts ?? 0)}
                    </EstadoDoValor>
                  );
                })()}
              </span>
            </div>
          </div>
        }
      />

      {/* Full-page skeleton */}
      {loading && renderPageSkeleton()}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p className="font-medium">Erro ao carregar dados</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Account permission warning */}
      {accountWarning && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-lg p-4">
          <p className="font-medium">{accountWarning.message}</p>
          {accountWarning.details && (
            <p className="text-sm mt-1 opacity-80">{accountWarning.details}</p>
          )}
          <p className="text-sm mt-2">
            Para liberar as métricas, o usuário do Meta deve ter acesso de
            administrador à Página do Facebook conectada ao Instagram Business e
            conceder as permissões do app.
          </p>
        </div>
      )}

      {/* ================================================================ */}
      {/* MAIN CONTENT                                                     */}
      {/* ================================================================ */}
      {!loading && !error && (
        <>
          {/* ROW 1: 7 KPI Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
            {renderMetricCard("seguidores", "Seguidores", "card-kpi-primary")}
            {renderMetricCard("seguindo", "Seguindo", "card-kpi-primary")}
            {renderMetricCard(
              "novosSeguidores",
              "Novos Seguidores",
              "card-kpi-primary",
            )}
            {renderMetricCard(
              "visitasAoPerfil",
              "Visitas Perfil",
              "card-kpi-primary",
            )}
            {renderMetricCard(
              "cliquesLinkBio",
              "Cliques no Link",
              "card-kpi-primary",
            )}
            {renderMetricCard("sessoes", "Sessões", "card-kpi-primary")}
            {renderMetricCardFromEngagement(
              "Engajamento de Mídia",
              engagementRaw?.total_interactions,
              "card-kpi-primary",
            )}
          </div>

          {/* ROW 2: 6 KPI Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {renderMetricCard(
              "impressoes",
              "Visualizações",
              "card-kpi-primary",
            )}
            {renderMetricCard("curtidas", "Curtidas", "card-kpi-primary")}
            {renderMetricCard("comentarios", "Comentários", "card-kpi-primary")}
            {renderMetricCard("salvos", "Salvos", "card-kpi-primary")}
            {renderMetricCard(
              "compartilhamentos",
              "Compartilhamentos",
              "card-kpi-primary",
            )}
            {renderMetricCardFromEngagement(
              "Alcance",
              engagementRaw?.total_reach,
              "card-kpi-primary",
            )}
          </div>

          {/* Engagement Rates + Formula (API only, fail-fast) */}
          <InstagramEngagementRates
            rates={engagementRates}
            totalPosts={Number(
              getCardMetric("totalPostsPublicados")?.value ?? 0,
            )}
            loading={loading}
          />

          {/* Demographics (componentized) */}
          <InstagramDemographics
            genderData={genderChartData}
            ageData={ageChartData}
            citiesData={citiesData}
            snapshotAt={demographicsSnapshotAt}
            loading={false}
          />

          {/* Posts Timeline (componentized with StandardizedTable) */}
          <InstagramPostsTimeline
            data={postsData}
            loading={postsLoading}
            totalRow={postsTotalRow}
            pagination={{
              current: postsPage,
              pageSize: 10,
              total: postsTotal,
              onPageChange: setPostsPage,
            }}
            onQueryChange={handlePostsQueryChange}
          />

          {/* Post Detail (componentized) */}
          <InstagramPostDetailCard
            data={postDetailData}
            loading={postsLoading}
          />

          {/* Best Times (componentized) */}
          <InstagramBestTimes
            bestDayData={MOCK_BEST_DAY_DATA}
            bestHourData={MOCK_BEST_HOUR_DATA}
            loading={false}
          />

          {/* Reels Section (API only, fail-fast) */}
          <InstagramReelsSection
            kpis={reelsKpis}
            loading={loading}
            erro={erroMetricas}
          />

          {/* Stories Table (API only, fail-fast) */}
          <InstagramStoriesTable
            data={storiesData}
            loading={storiesLoading}
            totalRow={storiesTotalRow}
            pagination={{
              current: storiesPage,
              pageSize: 10,
              total: storiesTotal,
              onPageChange: setStoriesPage,
            }}
            onQueryChange={handleStoriesQueryChange}
          />

          {/* Time Series Charts — real API data */}
          <Card>
            <CardHeader>
              <CardTitle>Alcance Por Dia</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollableChart>
                <TremorLineChart
                  className="h-64"
                  data={getTimeseriesPoints("alcance").map((point) => ({
                    ...point,
                    valor: point.value,
                  }))}
                  index="label"
                  categories={["valor"]}
                  colors={["emerald"]}
                  valueFormatter={(value) => value.toLocaleString("pt-BR")}
                  showLegend={true}
                  showGridLines={true}
                  showAnimation={true}
                  curveType="monotone"
                />
              </ScrollableChart>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Visualizações Por Dia</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollableChart>
                <TremorBarChart
                  className="h-64"
                  data={getTimeseriesPoints("visualizacoes").map((point) => ({
                    ...point,
                    valor: point.value,
                  }))}
                  index="label"
                  categories={["valor"]}
                  colors={["indigo"]}
                  valueFormatter={(value) => value.toLocaleString("pt-BR")}
                  showLegend={true}
                  showGridLines={true}
                  showAnimation={true}
                />
              </ScrollableChart>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Novos Seguidores Por Dia</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollableChart>
                <TremorLineChart
                  className="h-64"
                  data={getTimeseriesPoints("novos-seguidores").map(
                    (point) => ({ ...point, valor: point.value }),
                  )}
                  index="label"
                  categories={["valor"]}
                  colors={["amber"]}
                  valueFormatter={(value) => value.toLocaleString("pt-BR")}
                  showLegend={true}
                  showGridLines={true}
                  showAnimation={true}
                  curveType="monotone"
                />
              </ScrollableChart>
            </CardContent>
          </Card>

          {/* No data message */}
          {!hasCardData && !loading && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 text-center">
              <p className="font-medium">Nenhum dado disponível</p>
              <p className="text-sm">
                Ajuste o período do filtro ou aguarde a sincronização de dados
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstagramPage;
